import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { Select } from "@opendate-app/setlist";
import "../../TeamManager/styles/teamManagerStyles.css";
import SettingsPageWrapper from "../../../shared/SettingsPageWrapper";
import { updateTeamSetting } from "../../../shared/api/team";
import { toastSuccess, toastError } from "../../../shared/toastHelper";
import OfferTemplatesManager from "./OfferTemplatesManager";
import OfferFileNameBuilder from "./OfferFileNameBuilder";
import AgeRestrictionsSettings from '../../AgeRestrictionsSettings';

const RESALE_CAP_MINIMUM_MESSAGE = "The resale price cap must be at least 100%.";

const OFFER_SETTING_OPTIONS = [
  {
    field: 'show_hold_dates_on_offer',
    label: 'Show Hold Dates on PDF',
    description: 'Show the date of each hold in the generated Offer PDF.',
  },
  {
    field: 'show_hold_positions_on_offer',
    label: 'Show Hold Positions on PDF',
    description: 'Show the position label (H1, H2, etc.) of each hold in the generated Offer PDF.',
  },
  {
    field: 'auto_add_contacts_to_offer',
    label: 'Auto-Add Contacts to Offer',
    description: 'Automatically add contacts to offers when they are created.',
  },
];

const SHOW_OTHER_ARTISTS_OPTION = {
  field: 'show_other_artists_on_settlement',
  label: 'Show Other Artists on Settlement PDFs',
  description: "On a shared-pool settlement, list every act's Total Due on each artist's settlement PDF. Turn off so each artist sees only their own. Venues and individual settlements can override this.",
};

const venueOverrideSelectValue = (value) => (value === null || value === undefined ? '' : String(value));

const venueOverrideFromSelectValue = (value) => (value === '' ? null : value === 'true');

const SettingsOffersDetails = ({ team, offerV3, settingsPath, lastEditedAt, changelogVersions, venueOwnerships = [], selectedVenueOwnershipId, csrfToken, offerTemplates = [], ageRestrictionSettings = [], ageRestrictionVenueOwnerships = [], venueAgeRestrictionsByVenueOwnership = {} }) => {
  const [offerSettings, setOfferSettings] = useState({
    show_hold_dates_on_offer: team.show_hold_dates_on_offer ?? true,
    show_hold_positions_on_offer: team.show_hold_positions_on_offer ?? false,
    auto_add_contacts_to_offer: team.auto_add_contacts_to_offer ?? false,
    show_other_artists_on_settlement: team.show_other_artists_on_settlement ?? true,
  });

  const showsMultiArtistSettings = !!team.package?.multi_artist_v3_offers;
  const teamOfferSettingOptions = showsMultiArtistSettings
    ? [...OFFER_SETTING_OPTIONS, SHOW_OTHER_ARTISTS_OPTION]
    : OFFER_SETTING_OPTIONS;

  const [doorDealEnabled, setDoorDealEnabled] = useState(team.door_deal_label_enabled || false);
  const [doorDealText, setDoorDealText] = useState(team.door_deal_label_text || "Door Deal");
  const [teamResaleCap, setTeamResaleCap] = useState(team.resale_price_cap_percentage ?? "");
  const [savedTeamResaleCap, setSavedTeamResaleCap] = useState(team.resale_price_cap_percentage ?? "");

  const [venueOwnershipsLocal, setVenueOwnershipsLocal] = useState(venueOwnerships);
  const [selectedVenueId, setSelectedVenueId] = useState(selectedVenueOwnershipId || (venueOwnerships[0] && venueOwnerships[0].id));
  const [venueSettings, setVenueSettings] = useState(() => {
    const vo = venueOwnerships.find(v => v.id === (selectedVenueOwnershipId || (venueOwnerships[0] && venueOwnerships[0].id)));
    if (!vo) return {};
    const fee = parseFloat(vo.default_facility_fee);
    return {
      ...vo,
      default_facility_fee: !isNaN(fee) && fee > 0 ? fee.toFixed(2) : '',
    };
  });

  const handleVenueChange = useCallback((venueId) => {
    const id = parseInt(venueId);
    setSelectedVenueId(id);
    const vo = venueOwnershipsLocal.find(v => v.id === id);
    if (vo) {
      const fee = parseFloat(vo.default_facility_fee);
      setVenueSettings({
        ...vo,
        default_facility_fee: !isNaN(fee) && fee > 0 ? fee.toFixed(2) : '',
      });
    }
  }, [venueOwnershipsLocal]);

  const restoreSavedVenueSetting = useCallback((field) => {
    const saved = venueOwnershipsLocal.find(vo => vo.id === selectedVenueId);
    setVenueSettings(prev => ({ ...prev, [field]: saved ? saved[field] : null }));
  }, [venueOwnershipsLocal, selectedVenueId]);

  const updateVenueOwnershipSetting = useCallback(async (field, value) => {
    if (!selectedVenueId) return;
    setVenueSettings(prev => ({ ...prev, [field]: value }));
    try {
      await axios.patch(`/teams/${team.id}/venue_ownerships/${selectedVenueId}`, {
        venue_ownership: { [field]: value }
      }, {
        headers: csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {}
      });
      setVenueOwnershipsLocal(prev =>
        prev.map(vo => vo.id === selectedVenueId ? { ...vo, [field]: value } : vo)
      );
      toastSuccess("Setting saved.");
    } catch (error) {
      restoreSavedVenueSetting(field);
      const messages = error.response?.data;
      toastError(Array.isArray(messages) && messages.length > 0 ? messages.join(", ") : "Something went wrong. Please try again.");
    }
  }, [selectedVenueId, team.id, csrfToken, restoreSavedVenueSetting]);

  const handleVenueOtherArtistsChange = (selected) => {
    const next = venueOverrideFromSelectValue(selected ?? '');
    if (next === (venueSettings.show_other_artists_on_settlement ?? null)) return;
    updateVenueOwnershipSetting('show_other_artists_on_settlement', next);
  };

  const handleOfferSettingToggle = (field) => (e) => {
    const checked = e.target.checked;
    setOfferSettings((prev) => ({ ...prev, [field]: checked }));
    updateTeamSetting(team.id, { [field]: checked });
  };

  return (
    <SettingsPageWrapper settingsPath={settingsPath} title="Offers" lastEditedAt={lastEditedAt} changelogVersions={changelogVersions}>
      <div className="team-member-card">
        <div className="team-member-card-header">
          <div className="team-member-card-header-left">
            <div className="team-member-card-title-row">
              <h2 className="team-member-card-title">Offers</h2>
            </div>
            <p className="team-member-card-subtitle">
              Customize how your offers behave.
            </p>
          </div>
        </div>
        <hr style={{ margin: 0, border: 'none', borderTop: '1px solid var(--sl-border-secondary)' }} />

        <div style={{ padding: '32px 24px' }}>
          <div>
            <p className="mb-1 text-dark" style={{ fontSize: 16 }}>
              <strong>Settings for All Venues</strong>
            </p>
            <p className="m-0 small text-muted">
              Customize how your offers behave.
            </p>
          </div>
          <div className="mt-3 d-flex flex-column" style={{ gap: 12, maxWidth: 800 }}>
            {teamOfferSettingOptions.map((option) => (
              <label
                key={option.field}
                className="d-flex align-items-start m-0 p-3 rounded"
                style={{
                  border: '1px solid var(--sl-border-primary)',
                  cursor: 'pointer',
                  gap: 12,
                }}
              >
                <input
                  type="checkbox"
                  checked={offerSettings[option.field]}
                  onChange={handleOfferSettingToggle(option.field)}
                  className="team-checkbox mt-1"
                />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--sl-text-secondary)' }}>
                    {option.label}
                  </div>
                  <div className="small text-muted" style={{ marginTop: 2 }}>
                    {option.description}
                  </div>
                </div>
              </label>
            ))}
            <label
              className="d-flex align-items-start m-0 p-3 rounded"
              style={{
                border: '1px solid var(--sl-border-primary)',
                cursor: 'pointer',
                gap: 12,
              }}
            >
              <input
                type="checkbox"
                checked={doorDealEnabled}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setDoorDealEnabled(checked);
                  updateTeamSetting(team.id, { door_deal_label_enabled: checked });
                }}
                className="team-checkbox mt-1"
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--sl-text-secondary)' }}>
                  Door Deal Label
                </div>
                <div className="small text-muted" style={{ marginTop: 2 }}>
                  When the guarantee is $0, display a custom label instead of "$0.00".
                </div>
                {doorDealEnabled && (
                  <div className="mt-2" style={{ maxWidth: 300 }} onClick={(e) => e.preventDefault()}>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={doorDealText}
                      onChange={(e) => setDoorDealText(e.target.value)}
                      onBlur={() => {
                        updateTeamSetting(team.id, { door_deal_label_text: doorDealText });
                      }}
                      placeholder="Door Deal"
                    />
                  </div>
                )}
              </div>
            </label>

            <div className="p-3 rounded" style={{ border: '1px solid var(--sl-border-primary)' }}>
              <div id="team-resale-cap-label" style={{ fontSize: 14, fontWeight: 600, color: 'var(--sl-text-secondary)' }}>
                Fan-to-Fan Resale Price Cap
              </div>
              <div className="small text-muted" style={{ marginTop: 2 }}>
                Maximum fan-to-fan resale list price as a percent of the total the original buyer paid (face + tax + fees), applied to every venue and event unless overridden below. Leave blank to use the default of 110%.
              </div>
              <div className="input-group mt-2" style={{ maxWidth: 200 }}>
                <input
                  type="number"
                  min="100"
                  step="1"
                  aria-labelledby="team-resale-cap-label"
                  className="form-control"
                  placeholder="110"
                  value={teamResaleCap}
                  onChange={(e) => setTeamResaleCap(e.target.value)}
                  onBlur={(e) => {
                    const raw = e.target.value;
                    if (raw === "") {
                      updateTeamSetting(team.id, { resale_price_cap_percentage: null });
                      setSavedTeamResaleCap("");
                    } else {
                      const val = parseInt(raw, 10);
                      if (!isNaN(val) && val < 100) {
                        setTeamResaleCap(savedTeamResaleCap);
                        toastError(RESALE_CAP_MINIMUM_MESSAGE);
                      } else if (!isNaN(val)) {
                        updateTeamSetting(team.id, { resale_price_cap_percentage: val });
                        setSavedTeamResaleCap(val);
                      }
                    }
                  }}
                />
                <div className="input-group-append">
                  <span className="input-group-text">%</span>
                </div>
              </div>
            </div>

            <div className="p-3 rounded" style={{ border: '1px solid var(--sl-border-primary)' }}>
              <OfferTemplatesManager
                teamId={team.id}
                csrfToken={csrfToken}
                offerV3={offerV3}
                initialTemplates={offerTemplates}
              />
            </div>
          </div>

          {venueOwnerships.length > 0 && (
            <>
              <hr className="my-4" style={{ borderTop: '1px solid var(--sl-border-secondary)' }} />

              <div>
                <p className="mb-1 text-dark" style={{ fontSize: 16 }}>
                  <strong>Settings by Venue</strong>
                </p>
                <p className="m-0 small text-muted">
                  Configure offer and settlement settings per venue.
                </p>
              </div>

              <div className="mt-3" style={{ maxWidth: 280 }}>
                <select
                  className="team-form-select"
                  value={selectedVenueId || ''}
                  onChange={(e) => handleVenueChange(e.target.value)}
                >
                  {venueOwnerships.map((vo) => (
                    <option key={vo.id} value={vo.id}>{vo.name}</option>
                  ))}
                </select>
              </div>

              <div className="mt-3 d-flex flex-column" style={{ gap: 12, maxWidth: 800 }}>
                <label
                  className="d-flex align-items-start m-0 p-3 rounded"
                  style={{ border: '1px solid var(--sl-border-primary)', cursor: 'pointer', gap: 12 }}
                >
                  <input
                    type="checkbox"
                    checked={venueSettings.simplified_support_settlement || false}
                    onChange={(e) => updateVenueOwnershipSetting('simplified_support_settlement', e.target.checked)}
                    className="team-checkbox mt-1"
                  />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--sl-text-secondary)' }}>
                      Simplified Support Settlement
                    </div>
                    <div className="small text-muted" style={{ marginTop: 2 }}>
                      Use a simplified settlement format for support acts at this venue.
                    </div>
                  </div>
                </label>

                <label
                  className="d-flex align-items-start m-0 p-3 rounded"
                  style={{ border: '1px solid var(--sl-border-primary)', cursor: 'pointer', gap: 12 }}
                >
                  <input
                    type="checkbox"
                    checked={venueSettings.show_signature_lines_on_offer || false}
                    onChange={(e) => updateVenueOwnershipSetting('show_signature_lines_on_offer', e.target.checked)}
                    className="team-checkbox mt-1"
                  />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--sl-text-secondary)' }}>
                      Show Signature Lines on Offers
                    </div>
                    <div className="small text-muted" style={{ marginTop: 2 }}>
                      Display signature lines at the bottom of generated offer PDFs for this venue.
                    </div>
                  </div>
                </label>

                <label
                  className="d-flex align-items-start m-0 p-3 rounded"
                  style={{ border: '1px solid var(--sl-border-primary)', cursor: 'pointer', gap: 12 }}
                >
                  <input
                    type="checkbox"
                    checked={venueSettings.show_signature_lines_on_settlement || false}
                    onChange={(e) => updateVenueOwnershipSetting('show_signature_lines_on_settlement', e.target.checked)}
                    className="team-checkbox mt-1"
                  />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--sl-text-secondary)' }}>
                      Show Signature Lines on Settlements
                    </div>
                    <div className="small text-muted" style={{ marginTop: 2 }}>
                      Display signature lines at the bottom of generated settlement PDFs for this venue.
                    </div>
                  </div>
                </label>

                {showsMultiArtistSettings ? (
                  <div className="p-3 rounded" style={{ border: '1px solid var(--sl-border-primary)' }}>
                    <div id="venue-show-other-artists-label" style={{ fontSize: 14, fontWeight: 600, color: 'var(--sl-text-secondary)' }}>
                      Show Other Artists on Settlement PDFs
                    </div>
                    <div className="small text-muted" style={{ marginTop: 2 }}>
                      Overrides the team setting for shared-pool settlements at this venue. Each settlement can still override it.
                    </div>
                    <div className="mt-2" style={{ maxWidth: 280 }}>
                      <Select
                        aria-labelledby="venue-show-other-artists-label"
                        options={[
                          { value: '', label: `Use team setting (${offerSettings.show_other_artists_on_settlement ? 'Show' : 'Hide'})` },
                          { value: 'true', label: 'Show' },
                          { value: 'false', label: 'Hide' },
                        ]}
                        value={venueOverrideSelectValue(venueSettings.show_other_artists_on_settlement)}
                        onChange={handleVenueOtherArtistsChange}
                      />
                    </div>
                  </div>
                ) : null}

                <div className="p-3 rounded" style={{ border: '1px solid var(--sl-border-primary)' }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--sl-text-secondary)' }}>
                    Default Facility Fee
                  </div>
                  <div className="small text-muted" style={{ marginTop: 2 }}>
                    Per-ticket facility fee that auto-populates on new offers for this venue.
                  </div>
                  <div className="input-group mt-2" style={{ maxWidth: 200 }}>
                    <div className="input-group-prepend">
                      <span className="input-group-text">$</span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="form-control"
                      value={venueSettings.default_facility_fee || ''}
                      placeholder="0.00"
                      onChange={(e) => setVenueSettings(prev => ({ ...prev, default_facility_fee: e.target.value }))}
                      onBlur={(e) => {
                        const val = parseFloat(e.target.value);
                        if (!isNaN(val)) {
                          const formatted = val.toFixed(2);
                          setVenueSettings(prev => ({ ...prev, default_facility_fee: formatted }));
                          updateVenueOwnershipSetting('default_facility_fee', formatted);
                        } else if (e.target.value === '') {
                          updateVenueOwnershipSetting('default_facility_fee', 0);
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="p-3 rounded" style={{ border: '1px solid var(--sl-border-primary)' }}>
                  <div id="venue-resale-cap-label" style={{ fontSize: 14, fontWeight: 600, color: 'var(--sl-text-secondary)' }}>
                    Resale Price Cap
                  </div>
                  <div className="small text-muted" style={{ marginTop: 2 }}>
                    Maximum fan-to-fan resale price as a percent of the total the original buyer paid (face + tax + fees), for events at this venue. Leave blank to use the team default.
                  </div>
                  <div className="input-group mt-2" style={{ maxWidth: 200 }}>
                    <input
                      type="number"
                      min="100"
                      step="1"
                      aria-labelledby="venue-resale-cap-label"
                      className="form-control"
                      value={venueSettings.resale_price_cap_percentage ?? ''}
                      placeholder="Team default"
                      onChange={(e) => { const typedCap = e.target.value; setVenueSettings(prev => ({ ...prev, resale_price_cap_percentage: typedCap })); }}
                      onBlur={(e) => {
                        const raw = e.target.value;
                        if (raw === '') {
                          updateVenueOwnershipSetting('resale_price_cap_percentage', null);
                        } else {
                          const val = parseInt(raw, 10);
                          if (!isNaN(val) && val < 100) {
                            restoreSavedVenueSetting('resale_price_cap_percentage');
                            toastError(RESALE_CAP_MINIMUM_MESSAGE);
                          } else if (!isNaN(val)) {
                            updateVenueOwnershipSetting('resale_price_cap_percentage', val);
                          }
                        }
                      }}
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">%</span>
                    </div>
                  </div>
                </div>

                <div className="p-3 rounded" style={{ border: '1px solid var(--sl-border-primary)' }}>
                  <div className="custom-control custom-switch">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="default-tax-after-facility-fee"
                      checked={!!venueSettings.default_apply_tax_after_facility_fee}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setVenueSettings(prev => ({ ...prev, default_apply_tax_after_facility_fee: checked }));
                        updateVenueOwnershipSetting('default_apply_tax_after_facility_fee', checked);
                      }}
                    />
                    <label className="custom-control-label" htmlFor="default-tax-after-facility-fee" style={{ fontSize: 14, fontWeight: 600, color: 'var(--sl-text-secondary)' }}>
                      Apply tax after facility fee by default
                    </label>
                  </div>
                  <div className="small text-muted" style={{ marginTop: 2 }}>
                    New offers for this venue will apply admission tax after the facility fee (on the net gate) and add it on top as pass-through — it does not reduce the artist deal.
                  </div>
                </div>

                <OfferFileNameBuilder
                  value={venueSettings.offer_file_name_template || ''}
                  venueName={venueSettings.name}
                  venueCity={venueSettings.city}
                  onChange={(next) => updateVenueOwnershipSetting('offer_file_name_template', next)}
                />
              </div>
            </>
          )}

          <div style={{ height: 50 }} />
        </div>
      </div>

      <div style={{ marginTop: 50, marginBottom: 50 }}>
        <AgeRestrictionsSettings
          teamId={team.id}
          csrfToken={csrfToken}
          initialSettings={ageRestrictionSettings}
          venueOwnerships={ageRestrictionVenueOwnerships}
          initialVenueAgeRestrictions={venueAgeRestrictionsByVenueOwnership}
        />
      </div>
    </SettingsPageWrapper>
  );
};

SettingsOffersDetails.propTypes = {
  team: PropTypes.object.isRequired,
  offerV3: PropTypes.bool,
  settingsPath: PropTypes.string,
  lastEditedAt: PropTypes.string,
  changelogVersions: PropTypes.array,
  venueOwnerships: PropTypes.array,
  selectedVenueOwnershipId: PropTypes.number,
  csrfToken: PropTypes.string,
  offerTemplates: PropTypes.array,
  ageRestrictionSettings: PropTypes.array,
  ageRestrictionVenueOwnerships: PropTypes.array,
  venueAgeRestrictionsByVenueOwnership: PropTypes.object,
};

SettingsOffersDetails.defaultProps = {
  offerV3: null,
  settingsPath: null,
  lastEditedAt: null,
  changelogVersions: null,
  selectedVenueOwnershipId: null,
  csrfToken: null,
};

export default SettingsOffersDetails;
